import React, { useCallback, useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { CircularProgress } from '@mui/material';

import { cn } from 'components/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from 'components/ui/command';

import FilterControl from './FilterControl';

interface FilterCommandCardOptions {
  label: string;
  value: string;
  disabled?: boolean;
  groupHeading?: string;
}

interface MultiFilterCommandCardProps {
  isMulti: true;
  filterValue: string[];
  setFilterValue: (value: string[]) => void;
  maxSelections?: number;
}

interface SingleFilterCommandCardProps {
  isMulti?: false;
  filterValue: string | undefined;
  setFilterValue: (value: string | undefined) => void;
}

export type FilterCommandCardProps = {
  header: string;
  isLabelSearchBy?: boolean;
  options: FilterCommandCardOptions[];
  disabled?: boolean;
  isLoading?: boolean;
} & (MultiFilterCommandCardProps | SingleFilterCommandCardProps);

const SEARCH_PLACEHOLDER = 'Search';
const NO_RESULTS_TEXT = 'No results found.';
const NO_GROUP_HEADING_KEY = '__default';

const FilterCommandCard: React.FC<FilterCommandCardProps> = ({
  header,
  isLabelSearchBy = false,
  options = [],
  isMulti,
  filterValue,
  setFilterValue,
  disabled,
  isLoading,
  ...rest
}) => {
  const displayValue = useMemo(() => {
    if (!isMulti) {
      if (!filterValue) {
        return undefined;
      }
      const selectedOption = options.find((opt) => opt.value === filterValue);
      return selectedOption?.label ?? '';
    }

    if (filterValue.length === 0) {
      return undefined;
    }

    const firstSelectedOption = options.find(
      (opt) => opt.value === filterValue[0]
    );
    if (!firstSelectedOption) {
      return '';
    }

    if (filterValue.length === 1) {
      return firstSelectedOption.label;
    }

    return `${firstSelectedOption.label} and ${filterValue.length - 1} more`;
  }, [isMulti, filterValue, options]);

  const baseFilterValue = useMemo(() => {
    if (!isMulti) {
      return filterValue;
    }
    return filterValue[0];
  }, [isMulti, filterValue]);

  const isMaxReached = useMemo(() => {
    if (!isMulti) return false;

    const maxSelections = (rest as MultiFilterCommandCardProps).maxSelections;

    return maxSelections !== undefined && filterValue.length >= maxSelections;
  }, [isMulti, filterValue, rest]);

  const handleClear = useCallback(() => {
    if (isMulti) {
      setFilterValue([]);
    } else {
      setFilterValue(undefined);
    }
  }, [isMulti, setFilterValue]);

  const handleSelect = useCallback(
    (value: string) => {
      if (isMulti) {
        const newSelection = filterValue.includes(value)
          ? filterValue.filter((v) => v !== value)
          : [...filterValue, value];
        setFilterValue(newSelection);
      } else {
        setFilterValue(value);
      }
    },
    [isMulti, filterValue, setFilterValue]
  );

  const groupedOptions = useMemo(() => {
    return options.reduce<Record<string, FilterCommandCardOptions[]>>((acc, opt) => {
      const key = opt.groupHeading ?? NO_GROUP_HEADING_KEY;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(opt);
      return acc;
    }, {});
  }, [options]);

  return (
    <FilterControl
      header={header}
      filterValue={baseFilterValue}
      displayValue={displayValue}
      onClear={handleClear}
      isLabelSearchBy={isLabelSearchBy}
      disabled={disabled}
    >
      {(close) => (
        <Command>
          <CommandInput placeholder={SEARCH_PLACEHOLDER} />
          <CommandList>
            {isLoading ? (
              <div className="flex justify-center items-center py-6">
                <CircularProgress size={24} />
              </div>
            ) : (
              <>
                <CommandEmpty>{NO_RESULTS_TEXT}</CommandEmpty>
                {Object.entries(groupedOptions).map(
                  ([groupHeadingKey, groupedOptions]) => (
                    <CommandGroup
                      key={groupHeadingKey}
                      className="text-base-mutedForeground"
                      heading={groupHeadingKey !== NO_GROUP_HEADING_KEY ? groupHeadingKey : undefined}
                    >
                      {groupedOptions.map((option) => {
                        const isSelected = isMulti
                          ? filterValue.includes(option.value)
                          : filterValue === option.value;

                        const isDisabled =
                          option.disabled || (isMaxReached && !isSelected);

                        return (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => {
                              handleSelect(option.value);
                              if (!isMulti) {
                                close();
                              }
                            }}
                            disabled={isDisabled}
                            className={cn(
                              'cursor-pointer text-base-foreground hover:bg-base-accent',
                              isSelected && !isMulti && 'bg-base-accent',
                            )}
                            aria-selected={isSelected}
                            role="option"
                            aria-label={`${option.label}${
                              isSelected ? ' (selected)' : ''
                            }`}
                          >
                            {isMulti && (
                              <FontAwesomeIcon
                                icon={
                                  isSelected
                                    ? 'fa-solid fa-circle-check'
                                    : 'fa-regular fa-circle'
                                }
                                aria-hidden="true"
                              />
                            )}
                            <span>{option.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ),
                )}
              </>
            )}
          </CommandList>
        </Command>
      )}
    </FilterControl>
  );
};

export default FilterCommandCard;
